import Page from "@/components/Page";
import Resource from "@/components/Resource";
import Seomatic from "@/components/Seomatic";
import useTrack from "@/hooks/useTrack";
import Layout from "@/layouts/Layout";
import craftBatch from "@/lib/craftBatch";
import getCommonServerSideProps from "@/lib/getCommonServerSideProps";
import getSimilarResources from "@/lib/getSimilarResources";
import QueryResource from "@/queries/craft/QueryResource.graphql";
import QueryResourcesIndexPage from "@/queries/craft/QueryResourcesIndexPage.graphql";
import state from "@/state";
import { getServerSession } from "next-auth/next";
import Head from "next/head";
import { authOptions } from "pages/api/auth/[...nextauth]";
import React, { useEffect } from "react";

export async function getServerSideProps({
  preview,
  params,
  req,
  res,
  query,
  locale,
}) {
  const session = await getServerSession(req, res, authOptions);

  const commonProps = await getCommonServerSideProps({
    req,
    res,
    query,
    locale,
  });

  const queries = [
    {
      document: QueryResourcesIndexPage,
      variables: { site: commonProps.site },
    },
    {
      document: QueryResource,
      variables: {
        resourceSlug: params.resourceSlug,
        status: preview ? ["enabled", "pending", "disabled"] : ["enabled"],
      },
    },
  ];

  const response = await craftBatch(queries, query?.token);

  const { pageEntry } = response[0];
  const { resource, relatedCollections } = response[1];

  // return a 404 if not found
  if (!resource) {
    return {
      notFound: true,
    };
  }

  // If the resource is not public and we don't have a session, redirect to signin
  if (!session && !pageEntry.allowVisitorResourceAccess && !resource.isPublic) {
    return {
      redirect: {
        destination: `/auth/signin?destination=/resources/${resource.slug}`,
        permanent: false,
      },
    };
  }

  // Get similar resources
  resource.similarResources = await getSimilarResources(resource);

  return {
    props: {
      ...commonProps,
      pageEntry,
      resource,
      relatedCollections,
    },
  };
}

const ResourceEntry = ({ site, resource, relatedCollections }) => {
  // Set header text to dark
  useEffect(() => {
    state.header.theme = "dark";
  }, []);

  //	Track event
  useTrack(
    "View Resource",
    "Resource",
    resource.id,
    resource.title,
    resource.uri,
  );

  return (
    <Page withHero={false}>
      <Seomatic Head={Head} {...resource.seomatic} />
      {site !== "default" && (
        <Head>
          <meta key="robots" name="robots" content="none" />
        </Head>
      )}
      <Resource entry={resource} relatedCollections={relatedCollections} />
    </Page>
  );
};

const ResourceLayout = (page) => {
  const { navigation, site, siteLogo, organisation, hideYcnBranding } =
    page.props;

  return (
    <Layout
      site={site}
      navigation={navigation}
      siteLogo={siteLogo}
      organisation={organisation}
      hideYcnBranding={hideYcnBranding}
    >
      {page}
    </Layout>
  );
};

ResourceEntry.getLayout = ResourceLayout;

ResourceEntry.displayName = "ResourceEntry";

export default ResourceEntry;
